import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import QRCode from "qrcode";
import { ApiError } from "../../api/client";
import type { Challenge } from "../../api/auth";
import type { Me } from "../../api/types";
import { Notice } from "../../design/components";
import { useAuth } from "../../auth/AuthProvider";

export const CODE_WRONG = "That code is not right. Try again.";
export const CHALLENGE_EXPIRED = "That took too long. Sign in again.";
export const CHALLENGE_BURNED = "Too many wrong codes. Sign in again.";

function QrInformation({ url }: { url: string }) {
  const { size, path } = useMemo(() => {
    const { modules } = QRCode.create(url, { errorCorrectionLevel: "M" });
    let d = "";
    for (let y = 0; y < modules.size; y += 1) {
      for (let x = 0; x < modules.size; x += 1) {
        if (modules.get(y, x)) d += `M${x} ${y}h1v1h-1z`;
      }
    }
    return { size: modules.size, path: d };
  }, [url]);

  return (
    <svg
      className="enrol-qr"
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label="QR information for your authenticator app"
      shapeRendering="crispEdges"
    >
      <path d={path} fill="currentColor" />
    </svg>
  );
}

function messageFor(error: unknown): { text: string; fatal: boolean } {
  if (error instanceof ApiError) {
    if (error.code === "mfa_invalid") return { text: CODE_WRONG, fatal: false };
    if (error.code === "challenge_expired") return { text: CHALLENGE_EXPIRED, fatal: true };
    if (error.code === "challenge_burned") return { text: CHALLENGE_BURNED, fatal: true };
    if (error.code === "entity_choice_required") return { text: "Choose a company to continue.", fatal: false };
    return { text: error.message, fatal: error.status === 401 };
  }
  return { text: "Something went wrong. Try again.", fatal: false };
}

/** Leg two of sign-in: code, enrolment panel and company chooser, whichever the challenge asks for. */
export function ChallengeStep({
  challenge,
  onDone,
  onExpired,
}: {
  challenge: Challenge;
  onDone: (me: Me) => void;
  onExpired: (message: string) => void;
}) {
  const { completeChallenge } = useAuth();
  const needsCode = challenge.status !== "entity_choice_required";
  const needsCompany = challenge.memberships.length > 1;
  const enrolment = challenge.status === "mfa_enrolment_required" ? challenge : null;
  const codeRef = useRef<HTMLInputElement>(null);
  const companyRef = useRef<HTMLSelectElement>(null);
  const [code, setCode] = useState("");
  const [entityId, setEntityId] = useState(challenge.memberships[0]?.entityId ?? "");
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const focusFirst = () => (codeRef.current ?? companyRef.current)?.focus();

  useEffect(focusFirst, []);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setFailure(null);
    try {
      const me = await completeChallenge({
        challenge: challenge.challenge,
        ...(needsCode ? { code } : {}),
        ...(needsCompany ? { entityId } : {}),
      });
      onDone(me);
    } catch (error) {
      const { text, fatal } = messageFor(error);
      if (fatal) {
        onExpired(text);
        return;
      }
      setFailure(text);
      focusFirst();
    } finally {
      setPending(false);
    }
  }

  return (
    <form className="card signin-card" onSubmit={onSubmit}>
      <div className="card-body stack">
        {failure ? <Notice kind="fail">{failure}</Notice> : null}
        {enrolment ? (
          <section className="enrol" aria-labelledby="enrol-title">
            <h2 id="enrol-title">Set up your second factor</h2>
            <p>Scan the QR information with an authenticator app, or type the key by hand.</p>
            <QrInformation url={enrolment.otpauthUrl} />
            <p className="enrol-secret">
              <span>Manual key</span>
              <code>{enrolment.secret}</code>
            </p>
          </section>
        ) : null}
        {needsCompany ? (
          <label className="field" htmlFor="challenge-company">
            <span>Company</span>
            <select
              id="challenge-company"
              ref={companyRef}
              className="select"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              required
            >
              {challenge.memberships.map((m) => (
                <option key={m.entityId} value={m.entityId}>
                  {m.legalName}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {needsCode ? (
          <label className="field" htmlFor="challenge-code">
            <span>Six-digit code</span>
            <input
              id="challenge-code"
              ref={codeRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </label>
        ) : null}
        <button className="btn" type="submit" disabled={pending}>
          {needsCode ? "Confirm" : "Continue"}
        </button>
      </div>
    </form>
  );
}
